import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";

/**
 * Static elf decorations scattered down the marketing page.
 *
 * These are decorative cutout PNGs (transparent backgrounds) anchored
 * to specific marketing sections by DOM id, positioned absolutely
 * inside the existing `#marketing-cameo-root` so they scroll with the
 * page like the cinematic video cameos do.
 *
 * Differences from MarketingCharacterCameos:
 * - Image, not video → no autoplay, no decode cost beyond a single
 *   raster, no mix-blend-mode workaround needed (PNG alpha is honored
 *   directly by the compositor).
 * - Idle bob/sway is a tiny CSS transform animation, gated on
 *   prefers-reduced-motion so quiet motion users see a still elf.
 * - `pointer-events: none` so they can never block clicks on the
 *   real page content underneath.
 *
 * Each elf re-anchors on resize and on Suspense-driven section
 * mounts, mirroring the cameo logic so lazy-loaded sections still
 * land their elf in the right place.
 */

type ElfSide = "left" | "right";

interface ElfSpec {
  /** Stable id for keying + test selectors. */
  id: string;
  /** PNG src under /elves/. */
  src: string;
  /** Alt text — empty string keeps these decorative for AT. */
  alt: string;
  /** Section DOM id this elf anchors to. */
  anchor: string;
  /** Vertical offset in px relative to the section's top. */
  offsetPx: number;
  /** Which gutter the elf sits in. */
  side: ElfSide;
  /** Height in viewport-height units. */
  heightVh: number;
  /** Render on mobile? */
  mobile: boolean;
  /** Idle animation style. */
  motion: "bob" | "sway" | "hang";
  /** Render in front (z=20) or behind (z=5) page content. */
  layer: "front" | "back";
  /** Optional inline rotation in degrees, applied as a base transform. */
  rotateDeg?: number;
  /** Optional horizontal nudge from the gutter edge in px. */
  nudgePx?: number;
}

const ELVES: ElfSpec[] = [
  {
    // Upright lantern elf standing in the RIGHT gutter beside the
    // value strip. Anchored to the non-lazy `value` section so it
    // shows immediately on first paint. Gentle bob motion so the
    // lantern looks like it's swinging slightly. Asset is a clean
    // single-body cutout (the previous artwork had vestigial legs
    // at the top of the canvas — see git history for the fix).
    id: "elf-lantern",
    src: "/elves/elf-lantern.png",
    alt: "",
    anchor: "value",
    offsetPx: -30,
    side: "right",
    heightVh: 26,
    mobile: false,
    motion: "bob",
    layer: "front",
    nudgePx: 16,
  },
  {
    // Gift-bearing elf in the LEFT gutter of the sample-clues
    // section — a mid-page beat between How-It-Works and Pricing
    // that mirrors how Coalie's cameos cover the long stretch of
    // marketing real estate. Different costume (red jacket / green
    // hat) than the lantern elf so the page reads as multiple
    // distinct characters, not the same elf reused.
    id: "elf-gift",
    src: "/elves/elf-gift.png",
    alt: "",
    anchor: "sample-clues",
    offsetPx: 60,
    side: "left",
    heightVh: 24,
    mobile: false,
    motion: "bob",
    layer: "front",
    rotateDeg: -3,
    nudgePx: 14,
  },
  {
    // Sitting elf in the RIGHT gutter of the pricing section, like a
    // tiny mascot reviewing the tiers with you. (Originally drafted
    // for the left gutter — moved right to balance the gift elf in
    // the sample-clues left gutter just above.)
    id: "elf-sitting",
    src: "/elves/elf-sitting.png",
    alt: "",
    anchor: "pricing",
    offsetPx: 80,
    side: "right",
    heightVh: 24,
    mobile: false,
    motion: "bob",
    layer: "front",
    rotateDeg: -4,
    nudgePx: 12,
  },
  {
    // Jumping/celebrating elf near the final CTA — emotional payoff
    // beat: "yes, do this with us!". Right gutter. (Santa-peek cameo
    // now lives in the hero left gutter, not here.)
    //
    // Desktop only: at mobile widths the cranberry CTA card spans
    // nearly the full viewport, so the elf has nowhere to perch
    // without overlapping the eyebrow/headline and forcing the
    // "YOUR DECEMBER IS WAITING" line to wrap awkwardly. The hero
    // already carries the mobile elf cameo, so this one stays a
    // desktop-only flourish.
    id: "elf-jump",
    src: "/elves/elf-jump.png",
    alt: "",
    anchor: "final-cta",
    offsetPx: -40,
    side: "right",
    heightVh: 26,
    mobile: false,
    motion: "bob",
    layer: "front",
    rotateDeg: 4,
    nudgePx: 8,
  },
];

function ElfImage({ spec }: { spec: ElfSpec }) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [topPx, setTopPx] = useState<number | null>(null);
  const reducedMotion = usePrefersReducedMotion();

  // Anchor against the cameo root the same way MarketingCharacterCameos
  // does. Recompute on resize and on subtree mutations so lazy
  // sections (Suspense) move the elf into place once they mount.
  useLayoutEffect(() => {
    let raf = 0;
    const root = document.getElementById("marketing-cameo-root");
    if (!root) return;
    const compute = () => {
      const target = document.getElementById(spec.anchor);
      if (!target) return;
      const targetRect = target.getBoundingClientRect();
      const rootRect = root.getBoundingClientRect();
      setTopPx(targetRect.top - rootRect.top + spec.offsetPx);
    };
    compute();
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    };
    window.addEventListener("resize", onResize);
    const mo = new MutationObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    });
    mo.observe(root, { childList: true, subtree: true });
    return () => {
      window.removeEventListener("resize", onResize);
      mo.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [spec.anchor, spec.offsetPx]);

  if (topPx === null) return null;

  // Map motion → keyframe animation name. We rely on the global
  // jingle-* keyframes defined in index.css below.
  const animClass = reducedMotion
    ? ""
    : spec.motion === "hang"
      ? "jingle-elf-hang"
      : spec.motion === "sway"
        ? "jingle-elf-sway"
        : "jingle-elf-bob";

  const sideStyle: React.CSSProperties =
    spec.side === "left"
      ? { left: `${spec.nudgePx ?? 0}px` }
      : { right: `${spec.nudgePx ?? 0}px` };

  return (
    <div
      ref={wrapperRef}
      aria-hidden
      className={`pointer-events-none absolute ${spec.mobile ? "block" : "hidden lg:block"}`}
      style={{
        top: `${topPx}px`,
        height: `${spec.heightVh}vh`,
        zIndex: spec.layer === "front" ? 20 : 5,
        ...sideStyle,
      }}
      data-testid={`elf-${spec.id}`}
    >
      <img
        src={spec.src}
        alt={spec.alt}
        loading="lazy"
        decoding="async"
        className={animClass}
        style={{
          height: "100%",
          width: "auto",
          display: "block",
          transform: spec.rotateDeg ? `rotate(${spec.rotateDeg}deg)` : undefined,
          // Soft warm drop shadow grounds the elf against the dark
          // pine background so it doesn't look pasted-on.
          filter:
            "drop-shadow(0 8px 18px rgba(0,0,0,0.45)) drop-shadow(0 0 22px rgba(255, 180, 90, 0.15))",
        }}
      />
    </div>
  );
}

export function MarketingElfDecorations() {
  // Skip ALL elves until the marketing page has actually mounted the
  // anchor root. (StrictMode + lazy chunks mean the cameo root may
  // not be in the DOM on first render of this component.)
  const [ready, setReady] = useState(false);
  useEffect(() => {
    setReady(!!document.getElementById("marketing-cameo-root"));
  }, []);
  if (!ready) return null;
  return (
    <>
      {ELVES.map((e) => (
        <ElfImage key={e.id} spec={e} />
      ))}
    </>
  );
}
